import React, { useCallback, useState } from 'react';
import { Plus } from 'lucide-react';
import AnchoredPopover from './AnchoredPopover';
import TypeAhead, { type TypeAheadOption } from '../../../components/TypeAhead';
import ordersService, { type HazmatLookupItem } from '../../../services/orders.service';
import { useToast } from '../../../contexts/ToastContext';

interface Props {
  isOpen: boolean;
  pronumber: number | string;
  /** Trigger rect, for anchoring the popover. */
  buttonRect: DOMRect | null;
  onClose: () => void;
  /** Called after a successful add so the parent can re-pull order details. */
  onAdded: () => void | Promise<void>;
}

/**
 * Add-a-hazmat-line popover for Pro Details (Addl Info tab). The hazmat lines
 * list + per-row delete stay inline on the tab (their approval / do-not-haul
 * highlighting reads at a glance); only the UN-lookup add moved here, so the
 * "add" affordance matches the Grounded / Storage-Cleared popover style.
 * Ports V1 hazmatlookup + savehazmatinfo.
 */
const HazmatAddPopover: React.FC<Props> = ({ isOpen, pronumber, buttonRect, onClose, onAdded }) => {
  const { showToast } = useToast();
  const [unInput, setUnInput] = useState('');
  const [options, setOptions] = useState<TypeAheadOption[]>([]);
  const [picked, setPicked] = useState<HazmatLookupItem | null>(null);
  const [limited, setLimited] = useState<'Y' | 'N'>('N');
  const [busy, setBusy] = useState(false);

  const lookup = async (term: string) => {
    const t = term.trim();
    if (!t) { setOptions([]); return; }
    try {
      const { results } = await ordersService.hazmatLookup(t);
      setOptions(
        results.map((r) => ({
          value: `${r.hazmatUn}|${r.shippingName}|${r.hazmatClass}|${r.hazmatPkg}`,
          label: `${r.hazmatUn} — ${r.shippingName}`,
          description: `Class ${r.hazmatClass || '—'} · Pkg ${r.hazmatPkg || '—'}`,
          data: r,
        })),
      );
    } catch {
      setOptions([]);
    }
  };

  const handleAdd = useCallback(async () => {
    if (!picked || busy) return;
    setBusy(true);
    try {
      await ordersService.addHazmatLine(pronumber, {
        hazmatUn: picked.hazmatUn,
        shippingName: picked.shippingName,
        hazmatClass: picked.hazmatClass,
        hazmatPkg: picked.hazmatPkg,
        limitedQty: limited,
      });
      showToast({ type: 'success', message: `Hazmat UN ${picked.hazmatUn} added` });
      setPicked(null);
      setUnInput('');
      setOptions([]);
      setLimited('N');
      await onAdded();
      onClose();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      showToast({ type: 'error', message: typeof detail === 'string' ? detail : 'Failed to add hazmat line' });
    } finally {
      setBusy(false);
    }
  }, [picked, busy, pronumber, limited, showToast, onAdded, onClose]);

  return (
    <AnchoredPopover
      isOpen={isOpen}
      buttonRect={buttonRect}
      onClose={onClose}
      title="Add Hazmat Line"
      width={320}
      maxHeight={340}
    >
      {(containerEl) => (
        <div className="px-3 py-2 space-y-2">
          <TypeAhead
            freeText
            ariaLabel="Look up hazmat UN"
            placeholder="UN lookup…"
            compact
            value={unInput}
            options={options}
            portalContainer={containerEl}
            onChange={(v) => { setUnInput(v); setPicked(null); }}
            onSearch={lookup}
            onSelect={(opt) => {
              const r = opt.data as HazmatLookupItem;
              setPicked(r);
              setUnInput(r.hazmatUn);
              setOptions([]);
            }}
            showDescriptions
          />
          {picked && (
            <div className="text-[0.6875rem] text-slate-600 dark:text-gray-300">
              <div className="truncate" title={picked.shippingName}>{picked.shippingName || '—'}</div>
              <div className="flex items-center gap-2 text-slate-500 dark:text-gray-400">
                <span>Cls {picked.hazmatClass || '—'}</span>
                <span>Pkg {picked.hazmatPkg || '—'}</span>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-[0.6875rem] text-slate-600 dark:text-gray-300">
              Limited Qty
              <select
                className="border border-slate-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-[0.6875rem] px-1 py-0.5 disabled:opacity-50"
                value={limited}
                onChange={(e) => setLimited(e.target.value === 'Y' ? 'Y' : 'N')}
                disabled={!picked}
              >
                <option value="N">N</option>
                <option value="Y">Y</option>
              </select>
            </label>
            <button
              type="button"
              disabled={!picked || busy}
              onClick={() => void handleAdd()}
              className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded bg-blue-600 text-white text-xs hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-3 h-3" /> {busy ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
      )}
    </AnchoredPopover>
  );
};

export default HazmatAddPopover;
